import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Check, Info, CheckCircle } from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import {
  useResumeProfile,
  useUpdateResumePublic,
} from 'src/hooks/useResumeData';
import { MultiSelectDropdown } from 'src/components/common/MultiSelectDropdown';
import { Input } from 'src/components/ui/input';
import { Button } from 'src/components/ui/button';
import { FormField } from 'src/components/common/FormField';
import { validateResumeForm } from 'src/utils/resume-validation';
import { formatIndianNumber } from 'src/utils/numberFormat';
import { trackEvent } from 'src/utils/analytics';
import { useFormAbandonmentTracking } from 'src/hooks/useFormAbandonmentTracking';
import { PersonalInfoSection } from '../../components/resume/PersonalInfoSection';
import {
  EducationFields,
  AddEducationButton,
} from '../../components/resume/EducationFields';
import {
  WorkExperienceFields,
  AddExperienceButton,
} from '../../components/resume/WorkExperienceFields';
import { Section, FormActions } from '../../components/resume/common';
import { SkillsSection } from '../../components/resume/SkillsSection';
import { CertificationsSection } from '../../components/resume/CertificationsSection';
import { defaultValues } from '../../components/resume/constants';

// Validation rules and constants from original components
const validationRules = {
  // Personal Info
  firstname: { required: 'First name is required' },
  lastname: { required: 'Last name is required' },
  addressLine1: { required: 'Address Line 1 is required' },
  street: { required: 'Street is required' },
  city: { required: 'City is required' },
  email: {
    required: 'Email is required',
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Invalid email address',
    },
  },
  contact: {
    required: 'Phone number is required',
    pattern: {
      value: /^\+?[\d\s-]{10,}$/,
      message: 'Invalid phone number',
    },
  },
  dob: { required: 'Date of birth is required' },
  fathersname: { required: "Father's name is required" },
  state: { required: 'State is required' },
  zipCode: { required: 'ZIP code is required' },
};

const educationValidation = {
  'educations.*.institution': { required: 'Institution name is required' },
  'educations.*.qualification': { required: 'Qualification is required' },
  'educations.*.graduationYear': { required: 'Year of passing is required' },
  'educations.*.fieldsOfStudy': { required: 'Field of study is required' },
  'educations.*.maxGPA': { required: 'Max GPA is required' },
  'educations.*.earnedGPA': { required: 'Earned GPA is required' },
};

const INITIAL_MASTER_DATA = {
  skills: [],
  certifications: [],
  fieldsofstudies: [],
  fieldsofstudieshsc: [],
  city: [],
  state: [],
};

const getEmptyEducation = () => ({
  institution: '',
  qualification: '',
  graduationYear: '',
  fieldsOfStudy: '',
  gradeType: 'MARKS',
  maxMarks: '',
  earnedMarks: '',
  maxGPA: '',
  earnedGPA: '',
  board: '',
  university: '',
});

const getEmptyWorkExperience = () => ({
  company: '',
  role: '',
  employment_period: {
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    present: false,
  },
});

const generateKeys = {
  education: (edu, index) =>
    `education-${edu.institution || ''}-${edu.graduationYear || ''}-${index}`,
  workExperience: (exp, index) =>
    `work-${exp.company || ''}-${exp.role || ''}-${index}`,
};

// Map form data to API structure
function mapFormDataToApi(data) {
  return {
    first_name: data.firstname,
    middle_name: data.middlename,
    last_name: data.lastname,
    phone: data.contact,
    email: data.email,
    dob: data.dob,
    father_name: data.fathersname,
    address_line_1: data.addressLine1,
    street: data.street,
    city_id: data.city ? parseInt(data.city, 10) : null,
    state_id: data.state ? parseInt(data.state, 10) : null,
    zip_code: data.zipCode,
    educational_details: (data.educations || []).map((edu) => {
      let fieldOfStudyId = null;
      if (edu.qualification === 'TENTH') {
        fieldOfStudyId = null;
      } else if (edu.fieldsOfStudy) {
        fieldOfStudyId = parseInt(edu.fieldsOfStudy, 10);
      }
      return {
        institution: edu.institution,
        graduation_year: edu.graduationYear
          ? parseInt(edu.graduationYear, 10)
          : null,
        qualification_type: edu.qualification,
        field_of_study_id: fieldOfStudyId,
        score_type: edu.gradeType ? edu.gradeType.toUpperCase() : 'MARKS',
        max_score:
          edu.gradeType && edu.gradeType.toLowerCase() === 'marks'
            ? parseFloat(edu.maxMarks)
            : parseFloat(edu.maxGPA),
        actual_score:
          edu.gradeType && edu.gradeType.toLowerCase() === 'marks'
            ? parseFloat(edu.earnedMarks)
            : parseFloat(edu.earnedGPA),
        board_type:
          edu.qualification === 'TENTH' || edu.qualification === 'TWELFTH'
            ? edu.board
            : null,
        university:
          edu.qualification !== 'TENTH' && edu.qualification !== 'TWELFTH'
            ? edu.university
            : null,
      };
    }),
    skills: data.skills || [],
    certifications: data.certifications || [],
    no_certifications: !data.certifications?.length,
    // Add work_experiences if required by API
    // --- Add preferences fields ---
    expectedCtc: data.expectedCTC ? Number(data.expectedCTC) : undefined,
    currentCtc: data.currentCTC ? Number(data.currentCTC) : undefined,
    preferredWorkLocations: Array.isArray(data.preferredLocations)
      ? data.preferredLocations
          .map((id) => Number(id))
          .filter((id) => !Number.isNaN(id))
      : [],
    noticePeriod: data.noticePeriod ? Number(data.noticePeriod) : undefined,
  };
}

const ResumeProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { toast } = useToast();
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [showTermsError, setShowTermsError] = React.useState(false);

  // Use React Query to fetch resume profile
  const {
    data: resumeData,
    isLoading,
    refetch,
    error,
  } = useResumeProfile(token);

  // Use React Query for updating resume
  const updateResume = useUpdateResumePublic(resumeData?.candidateId, token);

  // Set up react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: resumeData || defaultValues,
    mode: 'onBlur',
    rules: {
      ...validationRules,
      ...educationValidation,
    },
  });

  const { markCompleted } = useFormAbandonmentTracking(
    'Resume Profile',
    'resume-profile',
    isDirty
  );

  // Sync form with resumeData
  React.useEffect(() => {
    if (resumeData) {
      reset(resumeData);
    }
  }, [resumeData, reset]);

  // Token validation
  React.useEffect(() => {
    if (!token) {
      toast({
        message: 'No verification token provided',
        variant: 'destructive',
      });
      navigate('/join-us');
    }
  }, [token, toast, navigate]);

  // Clear terms error once accepted
  useEffect(() => {
    if (acceptedTerms && showTermsError) {
      setShowTermsError(false);
    }
  }, [acceptedTerms, showTermsError]);

  const handleAddEducation = () => {
    const currentValues = watch();
    const updatedEducations = [
      ...currentValues.educations,
      getEmptyEducation(),
    ];

    reset({
      ...currentValues,
      educations: updatedEducations,
    });
  };

  const handleRemoveEducation = (index) => {
    const currentValues = watch();
    const updatedEducations = currentValues.educations.filter(
      (_, i) => i !== index
    );

    if (updatedEducations.length === 0) {
      updatedEducations.push(getEmptyEducation());
    }

    reset({
      ...currentValues,
      educations: updatedEducations,
    });
  };

  const handleAddWorkExperience = () => {
    const currentValues = watch();
    const updatedWorkExperiences = [
      ...(currentValues.workExperiences || []),
      getEmptyWorkExperience(),
    ];

    reset({
      ...currentValues,
      workExperiences: updatedWorkExperiences,
    });
  };

  const handleRemoveWorkExperience = (index) => {
    const currentValues = watch();
    const updatedWorkExperiences = currentValues.workExperiences.filter(
      (_, i) => i !== index
    );

    if (updatedWorkExperiences.length === 0) {
      updatedWorkExperiences.push(getEmptyWorkExperience());
    }

    reset({
      ...currentValues,
      workExperiences: updatedWorkExperiences,
    });
  };

  const handleSubmitForm = async (data) => {
    // Centralized validation
    const result = validateResumeForm(data);
    if (!result.isValid) {
      const message = result.message || 'Please fix the highlighted errors';
      toast({
        message,
        variant: 'destructive',
      });
      // try scroll to educations if relevant
      const el = document.getElementById('educations-0-institution');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const filteredWorkExperiences = data.workExperiences.filter(
      (exp) =>
        exp.company ||
        exp.role ||
        exp.employment_period.startMonth ||
        exp.employment_period.startYear ||
        exp.employment_period.endMonth ||
        exp.employment_period.endYear ||
        exp.employment_period.present
    );
    // Map form data to API structure
    const apiData = mapFormDataToApi({
      ...data,
      workExperiences:
        filteredWorkExperiences.length > 0 ? filteredWorkExperiences : [],
    });
    updateResume.mutate(apiData, {
      onSuccess: () => {
        trackEvent('form_success', 'Resume Profile', 'resume-profile');
        markCompleted();
        setShowSuccess(true);
        refetch();
      },
      onError: () => {
        trackEvent('form_error', 'Resume Profile', 'resume-profile');
        toast({
          message:
            error.message || 'Error updating resume. Please try again later',
          variant: 'destructive',
        });
      },
    });
  };

  // Helper: Recursively find the first error field name (dot notation)
  function getFirstErrorField(errorsObj, prefix = '') {
    let result = null;
    Object.entries(errorsObj).some(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (
        typeof value === 'object' &&
        value !== null &&
        value.type === undefined
      ) {
        const nested = getFirstErrorField(value, path);
        if (nested) {
          result = nested;
          return true; // break
        }
      } else {
        result = path;
        return true; // break
      }
      return false;
    });
    return result;
  }

  // Helper: Convert dot notation to DOM id (e.g., educations.0.institution -> educations-0-institution)
  function fieldNameToId(fieldName) {
    return fieldName.replace(/\./g, '-');
  }

  // Enhanced handleSubmit to scroll to first error
  const onSubmitWithScroll = handleSubmit(
    async (data) => {
      trackEvent('form_submit', 'Resume Profile', 'resume-profile');
      await handleSubmitForm(data);
    },
    (formErrors) => {
      trackEvent('form_submit', 'Resume Profile', 'resume-profile');
      // Find first error field
      const firstErrorField = getFirstErrorField(formErrors);
      if (firstErrorField) {
        const fieldId = fieldNameToId(firstErrorField);
        setTimeout(() => {
          const el = document.getElementById(fieldId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus?.();
          }
        }, 100); // 100ms delay to ensure DOM is updated
      }
    }
  );

  // Compute dynamic count of missing required fields (mirrors SubmitResume)
  const getMissingRequiredCount = () => {
    let count = 0;
    // Personal info required fields
    if (!watch('firstname')) count += 1;
    if (!watch('lastname')) count += 1;
    if (!watch('addressLine1')) count += 1;
    if (!watch('street')) count += 1;
    if (!watch('city')) count += 1;
    if (!watch('email')) count += 1;
    if (!watch('contact')) count += 1;
    if (!watch('dob')) count += 1;
    if (!watch('fathersname')) count += 1;
    if (!watch('state')) count += 1;
    if (!watch('zipCode')) count += 1;

    // Preferred locations
    const pref = watch('preferredLocations');
    if (!pref || pref.length === 0) count += 1;

    // Education required rows
    const edus = watch('educations') || [];
    edus.forEach((edu) => {
      if (!edu?.institution) count += 1;
      if (!edu?.qualification) count += 1;
      if (!edu?.graduationYear) count += 1;
      if (['TWELFTH', 'DEGREE', 'MASTERS'].includes(edu?.qualification || '')) {
        if (!edu?.fieldsOfStudy) count += 1;
      }
      if ((edu?.gradeType || 'MARKS').toUpperCase() === 'MARKS') {
        if (!edu?.maxMarks) count += 1;
        if (!edu?.earnedMarks) count += 1;
      } else {
        if (!edu?.maxGPA) count += 1;
        if (!edu?.earnedGPA) count += 1;
      }
      if (['TENTH', 'TWELFTH'].includes(edu?.qualification || '')) {
        if (!edu?.board) count += 1;
      } else if (!edu?.university) {
        count += 1;
      }
    });

    // Work experiences become required only if any field has value
    const exps = watch('workExperiences') || [];
    exps.forEach((exp) => {
      const hasAny = !!(
        exp?.company ||
        exp?.role ||
        exp?.employment_period?.startMonth ||
        exp?.employment_period?.startYear ||
        exp?.employment_period?.endMonth ||
        exp?.employment_period?.endYear ||
        exp?.employment_period?.present
      );
      if (hasAny) {
        if (!exp?.company) count += 1;
        if (!exp?.role) count += 1;
        const ep = exp?.employment_period || {};
        const isPresent = !!ep.present;
        const hasStart = ep.startMonth && ep.startYear;
        const hasEnd = ep.endMonth && ep.endYear;
        if (!hasStart) count += 1;
        if (!isPresent && !hasEnd) count += 1;
      }
    });

    return count;
  };

  // Primary action click handler to support dynamic "Next" and "Update Details"
  const handlePrimaryActionClick = () => {
    const missingCount = getMissingRequiredCount();
    if (missingCount > 0) {
      setShowTermsError(false);
      onSubmitWithScroll();
      return;
    }
    if (!acceptedTerms) {
      setShowTermsError(true);
      const termsEl = document.getElementById('terms');
      if (termsEl) {
        termsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        termsEl.focus?.();
      }
      return;
    }
    onSubmitWithScroll();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-full bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-sm text-gray-600">Loading resume data...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-full bg-gray-100">
        <p className="mt-4 text-sm text-red-600">
          {error.message || 'Error loading resume data'}
        </p>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="flex flex-col justify-center items-center min-h-full bg-gray-100">
        <Check className="h-12 w-12 text-green-500" />
        <h2 className="mt-4 text-2xl font-bold text-green-700">Success!</h2>
        <p className="mt-2 text-gray-700">
          Your resume has been updated successfully.
        </p>
      </div>
    );
  }

  // Use resumeData for masterData, formData, etc.
  const masterData = resumeData?.masterData || INITIAL_MASTER_DATA;
  const formData = watch();

  return (
    <div className="flex justify-center items-center bg-gray-100">
      <div className="container mx-auto px-4 py-8 pb-24 md:pb-8 max-w-4xl">
        <h1 className="text-2xl font-bold mb-6 text-left pl-[5px] md:pl-0">
          My Resume
        </h1>
        <form
          onSubmit={onSubmitWithScroll}
          className="space-y-4 text-left relative"
          noValidate
        >
          <PersonalInfoSection
            control={control}
            errors={errors}
            masterData={masterData}
          />
          <Section title="Educational Details">
            <div className="space-y-6">
              {formData.educations?.map((edu, index) => (
                <EducationFields
                  key={generateKeys.education(edu, index)}
                  education={edu}
                  index={index}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  masterData={masterData}
                  onRemove={
                    (formData.educations?.length || 0) > 1
                      ? () => handleRemoveEducation(index)
                      : null
                  }
                />
              ))}
              <AddEducationButton onClick={handleAddEducation} />
            </div>
          </Section>
          <Section title="Work Experience">
            <div className="space-y-6">
              {formData.workExperiences?.map((exp, index) => (
                <WorkExperienceFields
                  key={generateKeys.workExperience(exp, index)}
                  experience={exp}
                  index={index}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  onRemove={
                    (formData.workExperiences?.length || 0) > 1
                      ? () => handleRemoveWorkExperience(index)
                      : null
                  }
                />
              ))}
              <AddExperienceButton onClick={handleAddWorkExperience} />
            </div>
          </Section>
          {/* Preferences Section */}
          <Section title="Preferences">
            <div className="relative p-6 rounded-lg border border-gray-300 bg-gray-50 transition-all duration-200">
              {/* Preferred Location (Multi-Select) */}
              <FormField label="Preferred Location" required>
                <Controller
                  name="preferredLocations"
                  control={control}
                  rules={{
                    validate: (value) =>
                      value && value.length > 0
                        ? true
                        : 'At least one location is required',
                  }}
                  defaultValue={formData.preferredLocations || []}
                  render={({ field: { onChange, value } }) => {
                    // Transform masterData.preferedLocations to options
                    const locationOptions = (
                      masterData?.preferedLocations || []
                    ).map((loc) => ({
                      value: loc.id.toString(),
                      label: loc.name,
                    }));
                    return (
                      <MultiSelectDropdown
                        id="preferredLocations"
                        options={locationOptions}
                        placeholder="Select preferred locations..."
                        onChange={(selected) => {
                          onChange(selected.map((item) => item.value));
                        }}
                        value={locationOptions.filter((option) =>
                          value?.includes(option.value)
                        )}
                        maxDisplay={3}
                      />
                    );
                  }}
                />
                {errors?.preferredLocations && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.preferredLocations.message}
                  </p>
                )}
              </FormField>
              <div className="grid grid-cols-3 gap-4">
                {/* Current CTC (Optional) */}
                <FormField label="Current CTC (Annual)" className="text-xs">
                  <Controller
                    name="currentCTC"
                    control={control}
                    defaultValue={formData.currentCTC || ''}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter current CTC"
                        inputMode="numeric"
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          const limited = digitsOnly.slice(0, 8);
                          field.onChange(limited);
                        }}
                        value={formatIndianNumber(field.value)}
                        className="text-xs"
                      />
                    )}
                  />
                </FormField>

                {/* Expected CTC (Optional) */}
                <FormField label="Expected CTC (Annual)" className="text-xs">
                  <Controller
                    name="expectedCTC"
                    control={control}
                    defaultValue={formData.expectedCTC || ''}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter expected CTC"
                        inputMode="numeric"
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          const limited = digitsOnly.slice(0, 8);
                          field.onChange(limited);
                        }}
                        value={formatIndianNumber(field.value)}
                        className="text-xs"
                      />
                    )}
                  />
                </FormField>

                {/* Notice Period (days) 1-99 */}
                <FormField label="Notice Period (days)" className="text-xs">
                  <Controller
                    name="noticePeriod"
                    control={control}
                    defaultValue={formData.noticePeriod || ''}
                    rules={{
                      validate: (value) => {
                        if (
                          value === undefined ||
                          value === null ||
                          value === ''
                        )
                          return true;
                        const n = Number(value);
                        return (
                          (Number.isInteger(n) && n >= 0 && n <= 99) ||
                          'Enter 1–99 days'
                        );
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        min="1"
                        max="99"
                        step="1"
                        placeholder="Enter notice period"
                        inputMode="numeric"
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          const limited = digitsOnly.slice(0, 2);
                          if (limited === '') {
                            field.onChange('');
                            return;
                          }
                          const clamped = Math.min(
                            parseInt(limited, 10),
                            99
                          ).toString();
                          field.onChange(clamped);
                        }}
                        className="text-xs"
                      />
                    )}
                  />
                  {errors?.noticePeriod && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.noticePeriod.message}
                    </p>
                  )}
                </FormField>
              </div>
            </div>
          </Section>
          <SkillsSection control={control} masterData={masterData} />
          <CertificationsSection control={control} masterData={masterData} />
          <FormActions
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
            showTermsError={showTermsError}
            className="!mb-[7%]"
          />
          <div className="fixed inset-x-0 bottom-0 z-20 bg-white border-t border-gray-200 flex justify-between items-center py-3 px-4 md:px-[15%] lg:px-[23%]">
            <span className="text-sm text-gray-500 flex items-center">
              {getMissingRequiredCount() > 0 ? (
                <>
                  <Info className="mr-2 h-4 w-4 text-red-500" />
                  {`${getMissingRequiredCount()} required field(s) are missing`}
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                  Ready to submit
                </>
              )}
            </span>
            <Button
              type="button"
              onClick={handlePrimaryActionClick}
              className="bg-primary hover:bg-ah-brand-primary-hover"
            >
              {getMissingRequiredCount() === 0 && acceptedTerms
                ? 'Update Details'
                : 'Next'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ResumeProfile;
